// NotificationSettings — per-room notification-level popover.
//
// The trigger is the 24×24 bell icon in the room header's right
// cluster. Clicking the bell opens this popover anchored underneath
// it. The popover offers three radio rows: All / Mentions only /
// Mute. Selection writes a push-rule override via
// `setRoomNotificationLevel` and reflects the latest value via
// `getRoomNotificationLevel`.
//
// Keyboard: ↑/↓ cycle between rows, Enter selects. Esc closes.
// Focus: the popover mounts with focus on the currently selected
// row so screen readers announce the state immediately.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { usePortalPlacement } from "~/lib/portal-positioning";
import type { RoomNotificationLevel } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

export interface NotificationLevelOptionsProps {
  /** The currently persisted level — drives the checkmark + initial focus. */
  level: RoomNotificationLevel;
  /** Fires on click or Enter/Space. Caller owns persistence and (for the
   *  popover) closing the surface. */
  onSelect: (level: RoomNotificationLevel) => void;
  /** Accessible name for the radio group. */
  groupLabel: string;
  /** Move focus onto the selected row once on mount. The popover seeds
   *  focus so AT announces the current state immediately; the in-sheet
   *  variant leaves initial focus to the enclosing dialog. */
  autoFocusSelected?: boolean;
  /** `"sheet"` aligns heading and rows to the mobile sheet's 16px gutter
   *  with 52px touch targets; `"popover"` keeps the compact desktop rows. */
  variant?: "popover" | "sheet";
}

export function NotificationLevelOptions({
  level,
  onSelect,
  groupLabel,
  autoFocusSelected = false,
  variant = "popover",
}: NotificationLevelOptionsProps) {
  // Moving the caret does NOT persist — only click or Enter/Space calls
  // `onSelect`, because the server write is an expensive round-trip.
  const [focusedLevel, setFocusedLevel] = useState<RoomNotificationLevel>(level);
  const rowsRef = useRef(new Map<RoomNotificationLevel, HTMLDivElement | null>());
  const seededRef = useRef(false);

  // Sync the caret if `level` changes out-of-band (concurrent write from
  // another device) so it never drifts from the actual selection.
  useEffect(() => {
    setFocusedLevel(level);
  }, [level]);

  useEffect(() => {
    if (!autoFocusSelected || seededRef.current) return;
    seededRef.current = true;
    rowsRef.current.get(level)?.focus();
  }, [autoFocusSelected, level]);

  const handleKey = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const current = ROWS.findIndex((r) => r.level === focusedLevel);
        const delta = event.key === "ArrowDown" ? 1 : -1;
        const next = (current + delta + ROWS.length) % ROWS.length;
        const nextLevel = ROWS[next]!.level;
        setFocusedLevel(nextLevel);
        rowsRef.current.get(nextLevel)?.focus();
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(focusedLevel);
      }
    },
    [focusedLevel, onSelect],
  );

  return (
    <>
      <Heading variant={variant} />
      <div role="radiogroup" aria-label={groupLabel} onKeyDown={handleKey}>
        {ROWS.map((row) => (
          <Row
            key={row.level}
            variant={variant}
            spec={row}
            selected={row.level === level}
            registerRef={(el) => {
              rowsRef.current.set(row.level, el);
            }}
            onClick={() => onSelect(row.level)}
          />
        ))}
      </div>
    </>
  );
}

export interface NotificationSettingsProps {
  anchor: HTMLElement | null;
  level: RoomNotificationLevel;
  onSelect: (level: RoomNotificationLevel) => void;
  onClose: () => void;
}

interface RowSpec {
  level: RoomNotificationLevel;
  label: string;
  secondary?: string;
}

// Brief-pinned rows. Secondary copy sits under the primary row at
// `type-body-sm` and only the "Mentions only" + "Mute" rows carry it
// (per the ticket's Visual specs section).
const ROWS: readonly RowSpec[] = [
  { level: "all", label: "All messages" },
  { level: "mentions", label: "Mentions only", secondary: "Default" },
  { level: "mute", label: "Mute", secondary: "No notifications" },
];

const POPOVER_WIDTH = 240;
const VIEWPORT_EDGE = 8;
const ANCHOR_OFFSET = 4;
// Brief: popover height budget. The three rows + heading add up to
// 168px, but the real height is measured once mounted so the flip-up
// decision is honest. This constant is the minimum we assume before
// the first measure completes.
const POPOVER_HEIGHT_MIN = 168;

export function NotificationSettings({
  anchor,
  level,
  onSelect,
  onClose,
}: NotificationSettingsProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const portalRoot = usePortalRoot();
  const placement = usePortalPlacement({
    anchor,
    portalRoot,
    computeViewportCoords: (rect, surface) => {
      const height = popoverRef.current?.getBoundingClientRect().height ?? POPOVER_HEIGHT_MIN;
      const surfaceBottom = surface.offsetTop + surface.height;
      const surfaceRight = surface.offsetLeft + surface.width;
      const spaceBelow = surfaceBottom - rect.bottom;
      const flipsUp = spaceBelow < height + ANCHOR_OFFSET;
      const viewportTop = flipsUp
        ? Math.max(surface.offsetTop + VIEWPORT_EDGE, rect.top - height - ANCHOR_OFFSET)
        : rect.bottom + ANCHOR_OFFSET;
      const rawLeft = rect.right - POPOVER_WIDTH;
      const maxLeft = surfaceRight - POPOVER_WIDTH - VIEWPORT_EDGE;
      const viewportLeft = Math.max(surface.offsetLeft + VIEWPORT_EDGE, Math.min(rawLeft, maxLeft));
      return { viewportTop, viewportLeft };
    },
  });

  // Dismissal: Escape (only when focus is inside the popover) +
  // outside click. Scoping Escape to focus-inside means the
  // popover does not swallow Escape for other consumers (modals,
  // lightbox, thread panel) that mount above it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape" && e.key !== "Tab") return;
      const target = e.target as Node | null;
      if (!popoverRef.current?.contains(target)) return;
      e.preventDefault();
      onClose();
      anchor?.focus?.();
    }
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      onClose();
    }
    function onFocusOut(e: FocusEvent) {
      const related = e.relatedTarget as Node | null;
      if (!related) return;
      if (popoverRef.current?.contains(related) || anchor?.contains(related)) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [anchor, onClose]);

  const content = useMemo(() => {
    if (!placement) return null;
    return (
      <div
        ref={popoverRef}
        role="dialog"
        aria-label="Notification settings"
        style={{
          position: placement.position,
          top: placement.top,
          left: placement.left,
          width: POPOVER_WIDTH,
          zIndex: 50,
          padding: "var(--chat-space-2)",
          background: "var(--chat-surface-overlay)",
          border: "1px solid var(--chat-surface-border)",
          borderRadius: "var(--chat-radius-xl)",
          boxShadow: "var(--chat-shadow-popover)",
        }}
      >
        <NotificationLevelOptions
          level={level}
          groupLabel="Notifications"
          autoFocusSelected
          onSelect={(next) => {
            onSelect(next);
            onClose();
          }}
        />
      </div>
    );
  }, [placement, level, onSelect, onClose]);

  if (!anchor || !portalRoot) return null;
  return createPortal(content, portalRoot);
}

function Heading({ variant }: { variant: "popover" | "sheet" }) {
  return (
    <div
      style={{
        height: "var(--chat-space-6)",
        padding: variant === "sheet" ? "0 var(--chat-space-4)" : "0 var(--chat-space-2)",
        display: "flex",
        alignItems: "center",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "10px",
        lineHeight: "var(--chat-type-body-xs-line)",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      Notifications
    </div>
  );
}

function Row({
  spec,
  selected,
  onClick,
  registerRef,
  variant,
}: {
  variant: "popover" | "sheet";
  spec: RowSpec;
  selected: boolean;
  onClick: () => void;
  registerRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={registerRef}
      role="radio"
      tabIndex={selected ? 0 : -1}
      aria-checked={selected}
      onClick={onClick}
      className="chat-button"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: variant === "sheet" ? 52 : "var(--chat-space-10)",
        padding: variant === "sheet" ? "0 var(--chat-space-4)" : "0 var(--chat-space-2)",
        borderRadius: variant === "sheet" ? 0 : "var(--chat-radius-sm)",
        // On a phone the check carries the selection; a grey fill behind it
        // is a second signal (selection changes ink, not fill).
        background:
          selected && variant === "popover" ? "var(--chat-surface-raised)" : "transparent",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        cursor: "pointer",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <span
          style={{
            fontSize:
              variant === "sheet"
                ? "var(--chat-type-body-lg-size)"
                : "var(--chat-type-body-md-size)",
            lineHeight:
              variant === "sheet"
                ? "var(--chat-type-body-lg-line)"
                : "var(--chat-type-body-md-line)",
          }}
        >
          {spec.label}
        </span>
        {spec.secondary && (
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            {spec.secondary}
          </span>
        )}
      </div>
      {selected && (
        <Check
          size={16}
          strokeWidth={ICON_STROKE}
          aria-hidden
          style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
        />
      )}
    </div>
  );
}
